import clsx from 'clsx';
import { Tabs } from 'types/Tabs';
import ListingInfoPanel from './ListingInfoPanel';
import ContractRequestInfoPanel from './ContractRequest/ContractRequestInfoPanel';
import ContractSubmissionInfoPanel from './ContractSubmission/ContractSubmissionInfoPanel';
import { NswContractStage } from '@urbanx/agx-ui-components';
import { getEnumValue } from 'helpers/enumHelper';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractStatuses,
  FormType,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import type { MarketingPackageProjection } from '@urbanx/agx.marketing.client';
import { ReactNode, useState } from 'react';
import { AgxSpacer } from '@urbanx/agx.ui-components2';
import ConvertExchangeMethodButton, {
  showSolicitorExchangeConversion,
} from 'features/elements/ContractReviewAndSign/ConvertExchangeMethodButton';

interface ListingsPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
  selectedMarketingPackage?: MarketingPackageProjection;
  setSelectedMarketingPackage: (marketingPackage: MarketingPackageProjection | undefined) => void;
}

const ListingsPanel = ({
  campaign,
  currentTab,
  mobile = false,
  selectedMarketingPackage,
  setSelectedMarketingPackage,
}: ListingsPanelProps) => {
  const selectedContract = useNewSelectedContract();
  const [pinnedContractId, setPinnedContractId] = useState<string>();

  if (selectedContract == null) {
    return (
      <ListingInfoPanel
        campaign={campaign}
        currentTab={currentTab}
        mobile={mobile}
        selectedMarketingPackage={selectedMarketingPackage}
        setSelectedMarketingPackage={setSelectedMarketingPackage}
      />
    );
  }

  const selectedContractStage = selectedContract.contractStage as string | undefined;
  const nswContractStage =
    selectedContractStage == null
      ? undefined
      : (getEnumValue(NswContractStage, selectedContractStage) ?? selectedContractStage);
  const isNswSubmissionContract =
    selectedContract.contractId !== pinnedContractId &&
    campaign.state === CampaignDetailModelState.NSW &&
    (nswContractStage === NswContractStage.SubmissionIncomplete ||
      selectedContractStage === ContractStatuses.PendingCompliance ||
      nswContractStage === NswContractStage.PendingCompliance);

  let contractPanel: ReactNode;
  if (isNswSubmissionContract) {
    contractPanel = (
      <ContractSubmissionInfoPanel
        state={campaign.state ?? CampaignDetailModelState.QLD}
        contract={selectedContract}
        currentTab={currentTab}
        mobile={mobile}
      />
    );
  } else {
    switch (
      selectedContract.contractId === pinnedContractId
        ? InProgressFormModelFormType.ContractExchange
        : (selectedContract.inProgressForm?.formType ??
          InProgressFormModelFormType.ContractSubmission)
    ) {
      case InProgressFormModelFormType.ContractRequest:
      case InProgressFormModelFormType.ContractExchange:
        contractPanel = (
          <ContractRequestInfoPanel
            campaign={campaign}
            contract={selectedContract}
            currentTab={currentTab}
            mobile={mobile}
            onPinnedContractChange={setPinnedContractId}
          />
        );
        break;
      case InProgressFormModelFormType.ContractSubmission:
      default:
        contractPanel = (
          <ContractSubmissionInfoPanel
            state={campaign.state ?? CampaignDetailModelState.QLD}
            contract={selectedContract}
            currentTab={currentTab}
            mobile={mobile}
          />
        );
        break;
    }
  }

  return (
    <>
      {contractPanel}
      {campaign.campaignId &&
        selectedContract.contractId &&
        showSolicitorExchangeConversion(campaign, selectedContract) && (
          <AgxSpacer
            direction="vertical"
            contentAlignment="center"
            className={clsx({
              contractDraftActions: nswContractStage === NswContractStage.ContractDraft,
            })}
            fill
          >
            <ConvertExchangeMethodButton
              campaignId={campaign.campaignId}
              contractId={selectedContract.contractId}
              destination={FormType.ContractSubmission}
              variant={nswContractStage === NswContractStage.ContractDraft ? 'hollow' : 'primary'}
              stretch={mobile || nswContractStage === NswContractStage.ContractDraft}
              hasExchangeLetter={!!selectedContract.contractDocuments?.exchangeLetter}
              disabled={currentTab === Tabs.Archive}
              mobile={mobile}
            />
          </AgxSpacer>
        )}
    </>
  );
};

export default ListingsPanel;
